import { BuildFilled, InfoCircleOutlined } from '@ant-design/icons';
import { Card, Flex, Space, Spin, Tooltip, Typography } from 'antd';

import type { ModelProduct } from '@app/api/Products';
import type { AddProductFormValues } from '@app/pages/Warehouses/Products/components/AddProduct/types';

import type { FC, ReactNode } from 'react';

import '@pages/Warehouses/Products/components/AddProduct/styles/SimilarProducts.scss';

const { Text } = Typography;

type UnknownRecord = Record<string, unknown>;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const normalizeString = (value: string) => value.trim().toLowerCase();

const parseNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const normalized = trimmed.replace(',', '.');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const getStringField = (source: unknown, key: string): string | null => {
  if (!source || typeof source !== 'object') return null;
  const value = (source as UnknownRecord)[key];
  return isNonEmptyString(value) ? value : null;
};

const isEqualStrings = (
  a: string | null | undefined,
  b: string | null | undefined
) => {
  if (!isNonEmptyString(a) || !isNonEmptyString(b)) return false;
  return normalizeString(a) === normalizeString(b);
};

const isEqualNumbers = (
  a: number | null | undefined,
  b: number | null | undefined
) => {
  if (!isFiniteNumber(a) || !isFiniteNumber(b)) return false;
  return Math.abs(a - b) < 1e-9;
};

interface RowProps {
  label: string;
  value: ReactNode;
  isEqual?: boolean;
}

const Row: FC<RowProps> = ({ label, value, isEqual }) => {
  return (
    <Flex
      className="similar-product__row"
      align="center"
      justify="space-between"
      gap={12}
    >
      <Text
        type={isEqual ? 'warning' : 'secondary'}
        className="similar-product__rowLabel"
      >
        {label}:
      </Text>

      <Flex
        className="similar-product__rowRight"
        align="center"
        justify="end"
        gap={8}
      >
        <Text
          className="similar-product__rowValue"
          ellipsis
          type={isEqual ? 'warning' : undefined}
        >
          {value}
        </Text>
      </Flex>
      {isEqual ? (
        <Tooltip title={'Полностью совпадает'}>
          <BuildFilled className="similar-product__check" />
        </Tooltip>
      ) : null}
    </Flex>
  );
};

interface SimilarProductProps extends ModelProduct {
  currentValues: AddProductFormValues;
}

const SimilarProduct: FC<SimilarProductProps> = (product) => {
  const { currentValues } = product;

  const title = product.Name ?? product.NameShort ?? 'Без названия';

  const productCategoryTitle =
    product.ProductCategory ??
    getStringField(product, 'Category') ??
    getStringField(product, 'ProductCategoryTitle');

  const productBrandTitle =
    getStringField(product, 'ProductBrand') ??
    getStringField(product, 'Brand') ??
    getStringField(product, 'ProductBrandTitle');

  const unitTitle =
    product.UnitOfMeasurement ??
    getStringField(product, 'UnitOfMeasurementTitle') ??
    getStringField(product, 'UnitTitle');

  const amount = parseNullableNumber(product.Amount);
  const currentAmount = parseNullableNumber(currentValues?.Amount);

  const isCategoryEqual =
    isFiniteNumber(product.ProductCategoryId) &&
    isFiniteNumber(currentValues?.ProductCategoryId) &&
    product.ProductCategoryId === currentValues?.ProductCategoryId;

  const isBrandEqual =
    isFiniteNumber(product.ProductBrandId) &&
    isFiniteNumber(currentValues?.ProductBrandId) &&
    product.ProductBrandId === currentValues?.ProductBrandId;

  const isUnitEqual =
    isFiniteNumber(product.UnitOfMeasurementId) &&
    isFiniteNumber(currentValues?.UnitOfMeasurementId) &&
    product.UnitOfMeasurementId === currentValues?.UnitOfMeasurementId;

  const isTitleEqual =
    isEqualStrings(
      product.Name ?? product.NameShort ?? null,
      currentValues?.Name
    ) ||
    (isNonEmptyString(currentValues?.NameShort) &&
      isEqualStrings(
        product.NameShort ?? product.Name ?? null,
        currentValues?.NameShort
      ));

  return (
    <Card
      className="similar-product"
      size="small"
      title={
        <Flex align="center" justify="space-between" gap={12}>
          <Text
            ellipsis
            className="similar-product__title"
            type={isTitleEqual ? 'warning' : undefined}
          >
            {title}
          </Text>
          {isTitleEqual ? (
            <Tooltip title="Совпадает по названию">
              <BuildFilled className="similar-product__check" />
            </Tooltip>
          ) : null}
        </Flex>
      }
      classNames={{
        body: 'similar-product__body',
        header: 'similar-product__header',
      }}
    >
      <Flex vertical gap={8}>
        {isNonEmptyString(product.Article) && (
          <Row
            label="Артикул"
            value={product.Article}
            isEqual={isEqualStrings(product.Article, currentValues?.Article)}
          />
        )}

        {isNonEmptyString(product.BarCode) && (
          <Row
            label="Штрихкод"
            value={product.BarCode}
            isEqual={isEqualStrings(product.BarCode, currentValues?.BarCode)}
          />
        )}

        {isNonEmptyString(productCategoryTitle) && (
          <Row
            label="Категория"
            value={productCategoryTitle}
            isEqual={isCategoryEqual}
          />
        )}

        {isNonEmptyString(productBrandTitle) && (
          <Row label="Бренд" value={productBrandTitle} isEqual={isBrandEqual} />
        )}

        {isNonEmptyString(unitTitle) && (
          <Row
            label="Единицы измерения"
            value={unitTitle}
            isEqual={isUnitEqual}
          />
        )}

        {amount !== null && (
          <Row
            label="Масса/объём"
            value={amount}
            isEqual={isEqualNumbers(amount, currentAmount)}
          />
        )}
      </Flex>
    </Card>
  );
};

interface SimilarProductsProps {
  isLoading: boolean;
  similarProducts?: ModelProduct[] | null;
  currentValues: AddProductFormValues;
}

export const SimilarProducts: FC<SimilarProductsProps> = ({
  isLoading,
  similarProducts,
  currentValues,
}) => (
  <Spin spinning={isLoading} size="small">
    {similarProducts && similarProducts.length > 0 && (
      <div className="similar-products__wrapper">
        <Space>
          <InfoCircleOutlined className="similar-products__warningIcon" />
          <Text strong>Есть похожие товары</Text>
        </Space>

        <div className="similar-products__scroll">
          <Flex vertical gap={12} className="similar-products__list">
            {similarProducts?.map((product) => (
              <SimilarProduct
                key={product.Id}
                currentValues={currentValues}
                {...product}
              />
            ))}
          </Flex>
        </div>
      </div>
    )}
  </Spin>
);
